import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Stack
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';

const PasswordResetDialog = ({ open, onClose, member }) => {
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [message, setMessage] = useState(null);

  const handleClose = () => {
    setGeneratedUrl('');
    setGenerating(false);
    setCopied(false);
    setSendingEmail(false);
    setMessage(null);
    onClose();
  };

  const handleGenerateLink = async () => {
    setGenerating(true);
    setMessage(null);
    setCopied(false);
    setGeneratedUrl('');
    try {
      const result = await unifiedApi.generatePasswordResetUrl(member.id);
      if (result?.url) {
        setGeneratedUrl(result.url);
        setMessage({ type: 'success', text: 'Password reset link generated successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to generate password reset link' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to generate password reset link' });
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setMessage({ type: 'success', text: 'Link copied to clipboard' });
      setTimeout(() => setCopied(false), 3000);
    } catch {
      setMessage({ type: 'error', text: 'Failed to copy to clipboard' });
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    setMessage(null);
    try {
      const result = await unifiedApi.sendPasswordResetEmail(member.id);
      if (result?.success) {
        setMessage({ type: 'success', text: `Password reset email sent to ${member.email}` });
      } else {
        setMessage({ type: 'error', text: result?.message || 'Failed to send password reset email' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to send password reset email' });
    } finally {
      setSendingEmail(false);
    }
  };

  if (!member) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Password Reset</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            <strong>{member.firstName} {member.lastName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {member.email}
          </Typography>
        </Box>

        {message && (
          <Alert 
            severity={message.type} 
            onClose={() => setMessage(null)}
            sx={{ mb: 2 }}
          >
            {message.text}
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          {/* Option 1: Generate Link */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Option 1: Generate Link
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Generate a password reset link and copy it to share manually.
            </Typography>
            <Button
              variant="outlined"
              startIcon={generating ? <CircularProgress size={18} /> : <LinkIcon />}
              onClick={handleGenerateLink}
              disabled={generating || sendingEmail}
              fullWidth
            >
              {generating ? 'Generating...' : 'Generate Link'}
            </Button>

            {generatedUrl && (
              <Box sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={generatedUrl}
                  slotProps={{ input: { readOnly: true } }}
                  sx={{ mb: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                  onClick={handleCopyUrl}
                  color={copied ? 'success' : 'primary'}
                  fullWidth
                >
                  {copied ? 'Copied!' : 'Copy to Clipboard'}
                </Button>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Option 2: Send Email */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Option 2: Send Reset Email
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Send a branded password reset email to <strong>{member.email}</strong>.
            </Typography>
            <Button
              variant="outlined"
              startIcon={sendingEmail ? <CircularProgress size={18} /> : <EmailIcon />}
              onClick={handleSendEmail}
              disabled={sendingEmail || generating}
              fullWidth
            >
              {sendingEmail ? 'Sending...' : 'Send Reset Email'}
            </Button>
          </Box>
        </Stack>

        <Alert severity="info" sx={{ mt: 2 }}>
          The reset link expires in 24 hours. The user's password will not change unless they click the link and complete the reset.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordResetDialog;
